import { Callout } from "@octopusdeploy/design-system-components";
import type { ActionTemplateParameterResource, GitRefResource, VariableSetResource } from "@octopusdeploy/octopus-server-client";
import { ControlType, VariableType, Permission } from "@octopusdeploy/octopus-server-client";
import React from "react";
import type { FailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { isFailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import type { ProcessIdentifier } from "~/areas/projects/components/Process/types";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import type { FormFieldProps } from "~/components/form";
import { VariableSelect } from "~/components/form/VariableSelect/VariableSelect";
interface BaseVariableInputProps extends FormFieldProps<string> {
    gitRef: GitRefResource | undefined;
    label: string;
    defaultValueIndicator: JSX.Element | undefined;
    allowClear: boolean;
    processIdentifier: ProcessIdentifier;
    variableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    refreshVariables: (() => Promise<void>) | undefined;
    actionTemplateParameters: ActionTemplateParameterResource[];
}
export interface VariableInputProps extends BaseVariableInputProps {
    variableTypes: VariableType[];
    controlTypes: ControlType[];
}
const VariableInput: React.FC<VariableInputProps> = (props) => {
    const { value, gitRef, label, defaultValueIndicator, allowClear, processIdentifier, variableSet, libraryVariableSets, refreshVariables, variableTypes, controlTypes, actionTemplateParameters, ...rest } = props;
    const formProps = { ...rest, label };
    if (isFailedPermissionCheck(variableSet) || isFailedPermissionCheck(libraryVariableSets)) {
        return <Callout title={`${Permission.VariableView} and ${Permission.LibraryVariableSetView} are required to view selected variables`} type={"warning"}/>;
    }
    return (<React.Fragment>
            <VariableSelect value={value} processIdentifier={processIdentifier} variableSet={variableSet} libraryVariableSets={libraryVariableSets} gitRef={gitRef} allowClear={allowClear} refreshVariables={refreshVariables} variableTypes={variableTypes} controlTypes={controlTypes} actionTemplateParameters={actionTemplateParameters} {...formProps}/>
            {defaultValueIndicator}
        </React.Fragment>);
};
VariableInput.displayName = "VariableInput"
export function CertificateVariableInput(props: BaseVariableInputProps) {
    return <VariableInput {...props} variableTypes={[VariableType.Certificate]} controlTypes={[ControlType.Certificate]}/>;
}
export function AwsAccountVariableInput(props: BaseVariableInputProps) {
    return <VariableInput {...props} variableTypes={[VariableType.AmazonWebServicesAccount]} controlTypes={[ControlType.AmazonWebServicesAccount]}/>;
}
export function AzureAccountVariableInput(props: BaseVariableInputProps) {
    return <VariableInput {...props} variableTypes={[VariableType.AzureAccount]} controlTypes={[ControlType.AzureAccount]}/>;
}
export function GoogleCloudAccountVariableInput(props: BaseVariableInputProps) {
    return <VariableInput {...props} variableTypes={[VariableType.GoogleCloudAccount]} controlTypes={[ControlType.GoogleCloudAccount]}/>;
}
export function UsernamePasswordAccountVariableInput(props: BaseVariableInputProps) {
    return <VariableInput {...props} variableTypes={[VariableType.UsernamePasswordAccount]} controlTypes={[ControlType.UsernamePasswordAccount]}/>;
}
export function GenericOidcAccountVariableInput(props: BaseVariableInputProps) {
    return <VariableInput {...props} variableTypes={[VariableType.GenericOidcAccount]} controlTypes={[ControlType.GenericOidcAccount]}/>;
}
export function WorkerPoolVariableInput(props: BaseVariableInputProps) {
    return <VariableInput {...props} variableTypes={[VariableType.WorkerPool]} controlTypes={[ControlType.WorkerPool]}/>;
}
export function SensitiveVariableInput(props: BaseVariableInputProps) {
    return <VariableInput {...props} variableTypes={[VariableType.Sensitive]} controlTypes={[ControlType.Sensitive]}/>;
}
